export const NAV_LINKS = ["About", "Skills", "Experience", "Projects", "Journey", "Education", "Blog", "Contact"];

export const HERO = {
  badge: "Available for new opportunities",
  firstName: "Shubham",
  lastName: "Kumar",
  title: "Full Stack Developer · 3+ YOE",
  description:
    "Building scalable, high-performance web apps with React, Next.js, TypeScript, Node.js & GraphQL. Passionate about clean code, great UX, and shipping products that matter.",
  stats: [
    { number: "3+", label: "Years Experience" },
  ],
};

export const ABOUT = {
  intro:
    "I'm Shubham — a dedicated Full Stack Web Developer from India with 3+ years of professional experience specializing in the MERN stack. I've built products ranging from AI chatbots to multi-client automotive platforms.",
  summary:
    "My work at KOGO, Overdose Digital (via Welkin payroll), and now Enn Gee Ventures has shaped me into an engineer who deeply cares about performance, maintainability, and the humans using what I build.",
  highlights: [
    { icon: "⚡", title: "Performance First", description: "Reduced API response times from 20s to 2s in production." },
    { icon: "🏗️", title: "Architecture Minded", description: "Single Responsibility Principle with clean code and quality standards." },
    { icon: "📱", title: "Cross-Platform", description: "Pixel-perfect UI across Chrome, Safari, iOS, and Android." },
    { icon: "🤝", title: "Team Player", description: "Interviewer, code reviewer, and mentor for junior developers." },
  ],
};

export const SKILLS = {
  Frontend: {
    color: "from-violet-500 to-fuchsia-400",
    items: [
      { name: "React.js", icon: "⚛️" },
      { name: "Next.js", icon: "⚡" },
      { name: "TypeScript", icon: "📘" },
      { name: "JavaScript", icon: "🟨" },
      { name: "Tailwind CSS", icon: "🎨" },
      { name: "React Flow", icon: "🔀" },
      { name: "React DnD", icon: "🖱️" },
      { name: "Material UI", icon: "🎭" },
      { name: "Bootstrap", icon: "🅱️" },
      { name: "SCSS", icon: "💅" },
    ],
  },
  Backend: {
    color: "from-emerald-400 to-cyan-400",
    items: [
      { name: "Node.js", icon: "🟢" },
      { name: "Express.js", icon: "🚀" },
      { name: "GraphQL", icon: "⬡" },
      { name: "REST APIs", icon: "🔌" },
      { name: "Socket.io", icon: "🔁" },
    ],
  },
  Database: {
    color: "from-orange-400 to-yellow-400",
    items: [
      { name: "MongoDB", icon: "🍃" },
      { name: "Firebase", icon: "🔥" },
    ],
  },
  "State & Tools": {
    color: "from-pink-400 to-rose-500",
    items: [
      { name: "Redux Toolkit", icon: "🗄️" },
      { name: "React Query", icon: "🔍" },
      { name: "Axios", icon: "📡" },
      { name: "Git/GitHub", icon: "🐙" },
      { name: "SonarQube", icon: "🔬" },
      { name: "Chrome DevTools", icon: "🛠️" },
    ],
  },
};

export const EXPERIENCES = [
  {
    role: "Senior Software Engineer",
    company: "Enn Gee Ventures",
    location: "Chandigarh, India",
    date: "Sep 2025 – Present",
    tags: ["React.js", "TypeScript", "Node.js"],
    tagColors: ["#7c6af7", "#38d9a9", "#4fc3f7"],
    bullets: [
      "Leading full-stack development for enterprise-grade web applications.",
      "Architecting scalable systems using modern React and TypeScript patterns.",
      "Driving code quality standards and team engineering best practices.",
    ],
  },
  {
    role: "Frontend Developer",
    company: "KOGO Tech Labs",
    location: "New Delhi, India",
    date: "Jun 2024 – Aug 2025",
    tags: ["React", "TypeScript", "GraphQL", "SSR", "Mappls API"],
    tagColors: ["#7c6af7", "#38d9a9", "#ffa94d", "#f7506a", "#4fc3f7"],
    bullets: [
      "Built responsive UIs for Coforge, TVS & Bajaj Dominar — achieving 240% growth in customer acquisition.",
      "Optimized AI Chatbot modules for 15k+ MAU; cut API response time from 20s → 2s via GraphQL improvements.",
      "Designed 15+ reusable global components — boosted dev speed by 14%.",
      "Improved Core Web Vitals ~30% through SSR, semantic HTML, and strong quality standards.",
    ],
  },
  {
    role: "Frontend Developer",
    company: "Overdose Digital (via Welkin payroll)",
    location: "Melbourne, Australia (Remote)",
    date: "Aug 2022 – Jun 2024",
    tags: ["Next.js", "Tailwind CSS", "SCSS", "APIs"],
    tagColors: ["#7c6af7", "#38d9a9", "#ffa94d", "#4fc3f7"],
    bullets: [
      "Worked on Overdose Digital products while employed through Welkin Digital payroll, delivering remote Australian work from India.",
      "Achieved 20% improvement in website loading speed, enhancing user engagement.",
      "Collaborated cross-functionally to deliver safety technology solutions, resulting in 25% revenue increase.",
      "Led end-to-end feature development from requirements to production deployment.",
    ],
  },
  {
    role: "Frontend Developer (Trainee)",
    company: "neoG Camp",
    location: "Remote",
    date: "Jan 2022 – Aug 2022",
    tags: ["React", "JavaScript", "HTML/CSS"],
    tagColors: ["#7c6af7", "#38d9a9", "#ffa94d"],
    bullets: [
      "Completed intensive full-stack bootcamp focused on React and modern JavaScript.",
      "Built multiple projects applying component-based architecture and state management.",
    ],
  },
];

export const PROJECTS = [
  {
    name: "Hotel Booking Platform",
    type: "Web",
    emoji: "🏨",
    bg: "linear-gradient(135deg, #1a1a35 0%, #2a1a3a 100%)",
    desc: "Full-featured hotel reservation system with real-time availability, booking management, and SEO-optimized pages driving monthly traffic growth.",
    tech: ["React", "Next.js", "Node.js", "MongoDB", "Redux"],
    link: "#",
  },
  {
    name: "Car Rental Solution",
    type: "Web",
    emoji: "🚗",
    bg: "linear-gradient(135deg, #1a2a1a 0%, #1a3020 100%)",
    desc: "Multi-client car rental platform serving Bajaj, Hero, KTM, TVS, Mappls & Jawa with real-time booking, map integration and scalable APIs.",
    tech: ["React", "TypeScript", "Mappls API", "GraphQL"],
    link: "#",
  },
  {
    name: "AI Chatbot Interface",
    type: "AI",
    emoji: "🤖",
    bg: "linear-gradient(135deg, #1a1535 0%, #251a35 100%)",
    desc: "High-performance conversational AI UI serving 15k+ MAU with real-time streaming responses, chat history, and multi-modal inputs.",
    tech: ["React", "TypeScript", "Socket.io", "REST API"],
    link: "#",
  },
  {
    name: "Interactive Map Features",
    type: "Web",
    emoji: "🗺️",
    bg: "linear-gradient(135deg, #1a2535 0%, #1a2040 100%)",
    desc: "Built interactive map experiences using Mappls Maps API for leading automotive clients, boosting user interaction rates significantly.",
    tech: ["React", "Mappls API", "TypeScript", "CSS"],
    link: "#",
  },
  {
    name: "E-commerce Platform",
    type: "Web",
    emoji: "🛒",
    bg: "linear-gradient(135deg, #2a1a1a 0%, #351520 100%)",
    desc: "Scalable e-commerce solution with optimized performance modules, product discovery, and seamless checkout experience.",
    tech: ["Next.js", "React Query", "Node.js", "MongoDB"],
    link: "#",
  },
  {
    name: "Flow-based Canvas Tool",
    type: "Web",
    emoji: "🎨",
    bg: "linear-gradient(135deg, #1a2a20 0%, #102520 100%)",
    desc: "Node-based visual programming interface with drag-and-drop capabilities, custom node types, and real-time collaboration.",
    tech: ["React Flow", "React DnD", "TypeScript", "Socket.io"],
    link: "#",
  },
];

export const JOURNEY = [
  { year: "2018", icon: "📚", title: "Started B.Sc. Computer Science", desc: "Began the academic journey at Bihar National College — discovering the world of programming and falling in love with code.", num: "01" },
  { year: "2022", icon: "🎯", title: "Joined neoG Camp Bootcamp", desc: "Intensive full-stack program that transformed theoretical knowledge into real-world React and JavaScript expertise.", num: "02" },
  { year: "2022", icon: "🚀", title: "Joined Overdose Digital", desc: "Started a remote frontend role for Overdose Digital through Welkin payroll, shipping Australian product experiences from India.", num: "03" },
  { year: "2023", icon: "🌏", title: "Remote Global Delivery", desc: "Collaborated with international clients and Australian teams while on Welkin payroll for Overdose Digital.", num: "04" },
  { year: "2023", icon: "🎓", title: "Completed MCA", desc: "Graduated from LNCT Group of Colleges with Masters in Computer Applications while working full-time.", num: "05" },
  { year: "2024", icon: "⚡", title: "Joined KOGO — 15k+ Users", desc: "Scaled to a high-impact Frontend role serving 15k+ monthly active users across enterprise clients like TVS, Bajaj & Hero.", num: "06" },
  { year: "2025", icon: "👑", title: "Senior Software Engineer", desc: "Promoted to Senior role at Enn Gee Ventures — now leading architecture decisions and mentoring junior developers.", num: "07" },
  { year: "Now", icon: "🔭", title: "Building What's Next", desc: "Exploring AI-powered web experiences, contributing to open source, and always sharpening the craft.", num: "08" },
];

export const EDUCATION = [
  { icon: "🎓", degree: "Master of Computer Applications (MCA)", school: "LNCT Group of Colleges, Bhopal", field: "Computer Science", date: "Sep 2021 – Jun 2023" },
  { icon: "📘", degree: "Bachelor in Computer Science", school: "Bihar National College", field: "Computer Science", date: "Jul 2018 – Aug 2021" },
];

export const BLOGS = [
  {
    tag: "React",
    tagColor: "#7c6af7",
    title: "Building Performant React Apps: Lessons from 15k+ MAU Products",
    desc: "Practical optimizations I used to scale React applications — memoization strategies, bundle splitting, and Core Web Vitals wins.",
    date: "Coming soon",
    read: "8 min read",
  },
  {
    tag: "TypeScript",
    tagColor: "#4fc3f7",
    title: "TypeScript Patterns That Actually Scale",
    desc: "The real-world TypeScript patterns that made our codebase more maintainable — generics, discriminated unions, and module augmentation.",
    date: "Coming soon",
    read: "6 min read",
  },
  {
    tag: "AI",
    tagColor: "#38d9a9",
    title: "Integrating AI Chatbots in Production Web Apps",
    desc: "My experience building streaming AI chat interfaces — SSE, token handling, fallback strategies, and UI patterns that work.",
    date: "Coming soon",
    read: "10 min read",
  },
];

export const CONTACT_METHODS = [
  { icon: "📧", label: "Email", value: "shubham1bcabnc18@gmail.com", link: "mailto:shubham1bcabnc18@gmail.com", bg: "bg-fuchsia-500/10", color: "text-fuchsia-400" },
  { icon: "📱", label: "Phone", value: "+91 9852356258", link: "tel:+919852356258", bg: "bg-emerald-400/10", color: "text-emerald-300" },
  { icon: "📍", label: "Location", value: "Delhi, India", link: "#", bg: "bg-rose-500/10", color: "text-rose-300" },
];

export const SOCIALS = [
  { icon: "💼", link: "https://www.linkedin.com/in/shubhamkumar-6625b8217", label: "LinkedIn" },
  { icon: "🐙", link: "https://github.com/Shubhamkr2610", label: "GitHub" },
  { icon: "🌐", link: "https://shubhamkr2610.netlify.app/", label: "Portfolio" },
];
